import React, { useEffect, useState } from 'react';
import moment from 'moment';
import Modal from '../../../../shared/modal';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { useTheme } from '../../../../theme-context';
import TableActions from '../../../../shared/table/table-actions';
import Error from '../../../../shared/error';
import { tryToDeleteKnowledgeFileAiService, tryToFetchKnowledgeFilesAiService, tryToUploadKnowledgeFilesAiService } from '../../../../store/brokers/partners/knowledge-files-ai-service/knowledgeFilesSlice';

const extensionToType: Record<string, 'image' | 'video' | 'pdf' | 'doc' | 'spreadsheet' | 'text' | 'default'> = {
    // Images
    png: 'image',
    jpg: 'image',
    jpeg: 'image',
    gif: 'image',
    webp: 'image',
    svg: 'image',

    // Videos
    mp4: 'video',
    mov: 'video',
    avi: 'video',
    webm: 'video',

    // Documents
    pdf: 'pdf',
    doc: 'doc',
    docx: 'doc',
    txt: 'text',

    // Spreadsheets
    xls: 'spreadsheet',
    xlsx: 'spreadsheet',
    csv: 'spreadsheet',
};


const typeToIconPath: Record<string, string> = {
    image: '/assets/documents/image.svg',
    video: '/assets/documents/video.svg',
    pdf: '/assets/documents/pdf.svg',
    doc: '/assets/documents/docs.svg',
    text: '/assets/documents/text.svg',
    spreadsheet: '/assets/documents/excel.svg',
    default: '/assets/documents/default.svg',
};

const typeLabels: Record<string, string> = {
    image: 'Image',
    video: 'Video',
    pdf: 'PDF Document',
    doc: 'Word Document',
    text: 'Text File',
    spreadsheet: 'Spreadsheet',
    default: 'File',
};

interface IKnowledgeFilesModal {
    openModal: boolean;
    activeAgent: any;
    handleCloseModal: () => void;
}

const KnowledgeFilesModal = ({
    openModal,
    activeAgent,
    handleCloseModal,
}: IKnowledgeFilesModal) => {
    const { theme } = useTheme();
    const dispatch = useAppDispatch();
    const fileInputRef = React.useRef<HTMLInputElement | null>(null);
    const [files, setFiles] = useState<any>();
    const [deleteError, setDeleteError] = useState<string>();
    const [viewMode, setViewMode] = React.useState<'grid' | 'list'>('grid');
    const knowledgeFilesAiService = useAppSelector(state => state.knowledgeFilesAiService)

    const onGettingFilesData = async () => {
        try {
            await dispatch(tryToFetchKnowledgeFilesAiService()).unwrap();
        } catch (err) {
            console.error(err)
        }
    }

    useEffect(() => {
        setFiles(knowledgeFilesAiService?.knowledgeFilesAiService)
    }, [knowledgeFilesAiService?.knowledgeFilesAiService])

    useEffect(() => {
        onGettingFilesData()
    }, [])

    const getFileExtension = (fileName: string): string =>
        fileName.split('.').pop()?.toLowerCase() || '';

    const getFileType = (fileName: string): string => {
        const ext = getFileExtension(fileName);
        return extensionToType[ext] || 'default';
    };

    const getFileIconPath = (fileName: string): string => {
        const type = getFileType(fileName);
        return typeToIconPath[type];
    };

    const onCloseModal = () => {
        handleCloseModal();
        setDeleteError(undefined)
        setViewMode('grid')
    }

    const getTypeLabel = (fileName: string) => {
        const ext = getFileExtension(fileName);
        const typeKey = extensionToType[ext] || 'default';
        return typeLabels[typeKey];
    };

    const onClickDeleteFile = async (id: string) => {
        try {
            await dispatch(tryToDeleteKnowledgeFileAiService(id)).unwrap();
            setDeleteError(undefined)
        } catch (err) {
            setDeleteError(`${err}`)
        }
    }

    const onUploadClick = () => {
        fileInputRef.current?.click();
    };

    const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = event.target.files;
        if (!selectedFiles || selectedFiles.length === 0) return;

        for (let i = 0; i < selectedFiles.length; i++) {
            const formData = new FormData();
            formData.append('file', selectedFiles[i]);
            try {
                await dispatch(tryToUploadKnowledgeFilesAiService(formData)).unwrap();
            } catch (error) {
                console.error(`Error uploading file ${selectedFiles[i].name}`, error);
            }
        }
    };
	console.log({files})

    return (
        <Modal
            open={openModal}
            onClose={onCloseModal}
            contentContainerStyle={'!rounded-xl min-w-[95%] md:min-w-[70%] lg:min-w-[60%]'}>
            <div className={'p-2 max-w-[98vw] min-w-[95%] md:min-w-[70%] lg:min-w-[60%] min-h-[500px] '}>
                <div>
                    <p className='mb-2 font-semibold text-sm dark:text-primary-dark'>{activeAgent?.name} - Knowledge base</p>
                </div>
                <div className='flex justify-between items-center mb-4'>
                    <div>
                        <div onClick={onUploadClick} className='flex flex-row items-center justify-center px-2 py-1 border border-2 border-black rounded-xl dark:border-borderColor-darkMode hover:opacity-80 cursor-pointer'>
                            <img src={theme === 'dark' ? '/assets/documents/upload-docs-white.svg' : '/assets/documents/upload-docs.svg'} className='object-contain w-[20px]' />
                            <p className='font-semibold dark:text-primary-dark ml-2'>Upload new file</p>
                        </div>
                        <input
                            type='file'
                            multiple
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={onFileChange}
                            accept='*'
                        />
                    </div>
                    <div className='flex rounded-xl bg-[#f1eff1]'>
                        <div className={`px-2 py-2 hover:opacity-80 cursor-pointer ${viewMode === 'list' ? 'bg-[#c3e7ff] rounded-l-xl' : ''}`} onClick={() => setViewMode('list')}>
                            <img src={'/assets/documents/list.svg'} className='object-contain w-[20px]' />
                        </div>
                        <div className={`px-2 py-2 hover:opacity-80 cursor-pointer ${viewMode === 'grid' ? 'bg-[#c3e7ff] rounded-r-xl' : ''}`} onClick={() => setViewMode('grid')}>
                            <img src={'/assets/documents/grid.svg'} className='object-contain w-[20px]' />
                        </div>
                    </div>
                </div>
                {deleteError && <Error text={deleteError} />}
                <div>
                    {viewMode === 'grid' ?
                        <div className={viewMode === 'grid' ? 'grid grid-cols-2 md:grid-cols-4 gap-4' : 'flex flex-col gap-3'}>
                            {files && files?.length > 0 && files?.map((file: any, idx: number) => (
                                <div
                                    key={idx}
                                    className={`border rounded-lg p-4 shadow-sm hover:shadow-md cursor-pointer ${viewMode === 'grid' ? 'text-center' : 'flex items-center gap-4'}`}
                                >
                                    <div className='flex flex-row items-center mb-2 relative'>
                                        <img src={getFileIconPath(file?.name)} alt='icon' className='w-4 h-4 mr-2' />
                                        <div onClick={() => window?.open(file?.webUrl, '_blank')} className='text-sm truncate dark:text-primary-dark'>{file?.name}</div>
                                        <TableActions
                                            iconStyle='rotate-90'
                                            containerStyle='!mt-[-10px] !mr-[-20px] rounded-xl'>
                                            <div
                                                onClick={() => onClickDeleteFile(file?._id)}
                                                className='bg-gray-100 dark:bg-black shadow-xl rounded-xl px-5 py-2 cursor-pointer hover:opacity-80'>
                                                <p className='text-red-600'>Delete file</p>
                                            </div>
                                        </TableActions>
                                    </div>
                                    <div onClick={() => window?.open(file?.webUrl, '_blank')} className='min-h-[100px] flex justify-center items-center'>
                                        <img src={getFileIconPath(file?.name)} alt='icon' className='w-10 h-10' />
                                    </div>
                                    <div className='flex flex-row items-center mb-2'>
                                        <img src={'/assets/documents/documents-time.svg'} alt='icon' className='w-6 h-6 mr-2' />
                                        <div className='text-[12px] text-primary-light'>Created - {moment(file?.createdAt).format('MMM DD, YYYY')}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        : <div className='flex flex-col'>
                            <div className='hidden md:flex font-semibold text-xs text-gray-500 border-b py-2 px-2'>
                                <div className='w-1/12 dark:text-primary-dark'>Type</div>
                                <div className='w-6/12 dark:text-primary-dark'>Name</div>
                                <div className='w-3/12 dark:text-primary-dark'>Created At</div>
                                <div className='w-2/12 dark:text-primary-dark'>File Type</div>
                            </div>

                            {files && files?.length > 0 && files?.map((file: any, idx: number) => (
                                <div
                                    key={idx}
                                    className='flex items-center border-b hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer py-2 px-2 text-sm'
                                    onClick={() => window.open(file?.url, '_blank')}
                                >
                                    <div className='w-1/12 dark:text-primary-dark'>
                                        <img src={getFileIconPath(file?.name)} alt='icon' className='w-6 h-6' />
                                    </div>
                                    <div className='w-6/12 truncate dark:text-primary-dark'>{file?.name}</div>
                                    <div className='w-3/12 dark:text-primary-dark'>{moment(file?.createdAt).format('MMM DD, YYYY')}</div>
                                    <div className='w-2/12 dark:text-primary-dark'>{getTypeLabel(file?.name)}</div>
                                </div>
                            ))}
                        </div>
                    }
                </div>
            </div>
        </Modal >
    )
}
export default KnowledgeFilesModal;